"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import type { MouseEvent } from "react";
import { useCallback } from "react";
import { getCurrentSessionIdentity } from "@/actions/auth";
import type { CurrentShellProfile } from "@/actions/profile-shell";
import {
  SESSION_IDENTITY_QUERY_KEY,
  useAuthState,
} from "@/hooks/use-auth-state";
import {
  fetchShellProfile,
  SHELL_PROFILE_QUERY_KEY,
  SHELL_PROFILE_STALE_TIME_MS,
} from "@/hooks/use-shell-profile-data";
import { useAuthPrompt } from "./auth-prompt-provider";

type GateOptions = {
  /** Destination, also used as the post-sign-in callback URL. */
  href: string;
  title?: string;
  description?: string;
  /**
   * Whether the destination needs a *finished* profile, or merely a session.
   *
   * Defaults to `true`, which matches every social destination. Pass `false`
   * for anything a viewer bought — the wallet above all. Booking is gated on
   * identity, not on a profile, so a viewer can hold real tickets with an
   * unfinished profile, and "Finish your profile" is the wrong answer when
   * they tap through to look at them. See `plans/guest-checkout-auth.md`.
   */
  requireProfile?: boolean;
  /** Specialized sign-in flow that must survive every provider round-trip. */
  authIntent?: "sign-in" | "ticket-claim";
};

/**
 * Click guard for links into routes protected by `requireUser` (wallet,
 * notifications, …).
 *
 * The naive version — `if (authStatus !== "unauthenticated") return` — leaks two
 * ways, and both dump the viewer somewhere they did not ask to go:
 *
 *   1. `authStatus` is `"unknown"` until the session query resolves, which on a
 *      cold native launch is the whole first second or two. Every tap in that
 *      window navigated, and the server guard then bounced the viewer to `/` or
 *      `/onboarding` instead of showing the sign-in drawer.
 *   2. A signed-in viewer whose profile is incomplete passes the check, so
 *      `requireUser` redirected them into onboarding with no explanation.
 *
 * So the fast path is "confirmed signed in AND confirmed complete"; anything
 * short of that resolves the real state first (`fetchQuery` reuses the cache and
 * dedupes with the shell's in-flight request) and then either navigates or opens
 * the drawer with the intent that matches what it found.
 */
export function useAuthGatedLink() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { openAuthPrompt } = useAuthPrompt();
  const { authStatus } = useAuthState();

  const resolveAccess = useCallback(async () => {
    const identity = await queryClient
      .fetchQuery({
        queryKey: SESSION_IDENTITY_QUERY_KEY,
        queryFn: getCurrentSessionIdentity,
        staleTime: 60_000,
      })
      .catch(() => null);

    if (!identity) return { signedIn: false, profileComplete: false };

    const profile = await queryClient
      .fetchQuery({
        queryKey: SHELL_PROFILE_QUERY_KEY,
        queryFn: fetchShellProfile,
        staleTime: SHELL_PROFILE_STALE_TIME_MS,
      })
      .catch(() => null);

    // A read failure must not strand a signed-in viewer on the drawer: let the
    // navigation through and defer to the server guard, which is authoritative.
    return {
      signedIn: true,
      profileComplete: profile ? profile.profileComplete : true,
    };
  }, [queryClient]);

  const guardNavigation = useCallback(
    (event: MouseEvent<HTMLElement>, options: GateOptions) => {
      const cached = queryClient.getQueryData<CurrentShellProfile | null>(
        SHELL_PROFILE_QUERY_KEY,
      );
      const needsProfile = options.requireProfile ?? true;

      // Fast path: both facts already known, so the Link navigates natively and
      // keeps its prefetch / pending-state behaviour.
      if (
        authStatus === "authenticated" &&
        (!needsProfile || cached?.profileComplete)
      ) {
        return;
      }

      event.preventDefault();

      void resolveAccess().then(({ signedIn, profileComplete }) => {
        if (!signedIn) {
          openAuthPrompt({
            intent: options.authIntent ?? "sign-in",
            title: options.title,
            description: options.description,
            callbackURL: options.href,
          });
          return;
        }

        if (needsProfile && !profileComplete) {
          openAuthPrompt({
            intent: "finish-profile",
            callbackURL: options.href,
          });
          return;
        }

        router.push(options.href);
      });
    },
    [authStatus, openAuthPrompt, queryClient, resolveAccess, router],
  );

  return { guardNavigation };
}
